import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  Security as SecurityIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const Profile = () => {
  const { admin, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [showDisable2FADialog, setShowDisable2FADialog] = useState(false);
  const [passwordForDisable, setPasswordForDisable] = useState('');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('All password fields are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    try {
      setLoading(true);
      await authAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      
      toast.success('Password changed successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    try {
      setSetupLoading(true);
      const response = await authAPI.setup2FA();
      setQrCode(response.data.data.qrCodeUrl);
      setShow2FADialog(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start 2FA setup');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    if (!twoFactorToken || twoFactorToken.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    try {
      setSetupLoading(true);
      await authAPI.enable2FA(twoFactorToken);
      toast.success('2FA enabled successfully');
      setShow2FADialog(false);
      setTwoFactorToken('');
      checkAuth(); // Refresh profile
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to enable 2FA');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!passwordForDisable) {
      toast.error('Password is required to disable 2FA');
      return;
    }

    try {
      setSetupLoading(true);
      await authAPI.disable2FA(passwordForDisable);
      toast.success('2FA disabled successfully');
      setShowDisable2FADialog(false);
      setPasswordForDisable('');
      checkAuth(); // Refresh profile
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to disable 2FA');
    } finally {
      setSetupLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Profile Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  mr: 2,
                }}
              >
                <PersonIcon sx={{ fontSize: 48 }} />
              </Avatar>
              <Box>
                <Typography variant="h5">{admin?.username}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {admin?.role === 'super_admin' ? 'Super Administrator' : 'Administrator'}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Username
                </Typography>
                <Typography variant="body1">{admin?.username}</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">{admin?.email}</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">
                  Role
                </Typography>
                <Typography variant="body1">
                  {admin?.role === 'super_admin' ? 'Super Administrator' : 'Administrator'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">
                  Account Created
                </Typography>
                <Typography variant="body1">
                  {admin?.createdAt ? new Date(admin.createdAt).toLocaleDateString() : '-'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">
                  Last Login
                </Typography>
                <Typography variant="body1">
                  {admin?.lastLogin ? new Date(admin.lastLogin).toLocaleString() : '-'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Change Password */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Change Password</Typography>
            </Box>

            <form onSubmit={handlePasswordChange}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Current Password"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  }
                  fullWidth
                  required
                />

                <TextField
                  label="New Password"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  }
                  fullWidth
                  required
                  helperText="Minimum 6 characters"
                />

                <TextField
                  label="Confirm New Password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  fullWidth
                  required
                />

                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </Button>
              </Box>
            </form>
          </Paper>

          {/* Two-Factor Authentication */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Two-Factor Authentication (2FA)</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" paragraph>
                Two-factor authentication adds an extra layer of security to your account. 
                In addition to your password, you'll need to enter a code from an authenticator app.
              </Typography>

              {admin?.isTwoFactorEnabled ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    2FA is currently enabled on your account.
                  </Alert>
                  <Button 
                    variant="outlined" 
                    color="error" 
                    onClick={() => setShowDisable2FADialog(true)}
                  >
                    Disable 2FA
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    2FA is not enabled. We highly recommend enabling it for better security.
                  </Alert>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<QrCodeIcon />}
                    onClick={handleSetup2FA}
                    disabled={setupLoading}
                  >
                    {setupLoading ? 'Loading...' : 'Setup 2FA'}
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Security Tips */}
          <Paper sx={{ p: 3, mt: 3, bgcolor: 'info.light' }}>
            <Typography variant="h6" gutterBottom>
              Security Tips
            </Typography>
            <Typography variant="body2" component="div">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Use a strong password with at least 8 characters</li>
                <li>Include uppercase, lowercase, numbers, and symbols</li>
                <li>Don't reuse passwords from other accounts</li>
                <li>Change your password regularly</li>
                <li>Never share your password with anyone</li>
              </ul>
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FADialog} onClose={() => setShow2FADialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Setup Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, etc.)
            </Typography>
            {qrCode && (
              <Box 
                component="img" 
                src={qrCode} 
                alt="2FA QR Code" 
                sx={{ width: 200, height: 200, mx: 'auto', display: 'block' }}
              />
            )}
            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
              After scanning, enter the 6-digit verification code:
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              value={twoFactorToken}
              onChange={(e) => setTwoFactorToken(e.target.value)}
              placeholder="000000"
              inputProps={{ maxLength: 6 }}
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShow2FADialog(false)}>Cancel</Button>
          <Button 
            onClick={handleEnable2FA} 
            variant="contained" 
            disabled={setupLoading || twoFactorToken.length !== 6}
          >
            {setupLoading ? <CircularProgress size={24} /> : 'Verify and Enable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2FA Disable Dialog */}
      <Dialog open={showDisable2FADialog} onClose={() => setShowDisable2FADialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            To disable 2FA, please enter your account password for security verification.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Your Password"
            value={passwordForDisable}
            onChange={(e) => setPasswordForDisable(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisable2FADialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDisable2FA} 
            variant="contained" 
            color="error"
            disabled={setupLoading || !passwordForDisable}
          >
            {setupLoading ? <CircularProgress size={24} /> : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;